// TreatmentRecord.jsx - Staff UI for recording a treatment after a visit
// Opens as a full-screen modal from an appointment.
// Tabs: Chart / Photos / Notes
// Speed-optimized: pick a product → every face tap drops that product

import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import FaceChart from "./FaceChart";
import NoteTemplatePicker from "./NoteTemplatePicker";
import { fmtAppointmentDate } from "./dateUtils";

const C = {
  bg:"#f8f5f0", surface:"#fff", card:"#fff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6f2e",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", blueL:"rgba(37,99,235,0.08)",
  purple:"#7c3aed", text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const inp = {width:"100%",padding:"9px 12px",background:C.light,border:"1px solid "+C.border,borderRadius:7,fontSize:13,color:C.text,fontFamily:F,outline:"none",boxSizing:"border-box"};

// Quick post-care templates
const POST_CARE_TEMPLATES = [
  { label: "Botox aftercare", text: "• Avoid lying down for 4 hours\n• No vigorous exercise for 24 hours\n• Don't massage or rub the area for 24 hours\n• Avoid alcohol for 24 hours\n• Results begin in 3-7 days, full effect in 2 weeks" },
  { label: "Filler aftercare", text: "• Apply ice gently for 10 min if swelling\n• Avoid heat (sauna, hot yoga) for 24 hours\n• No facial massage for 2 weeks\n• Avoid blood thinners (alcohol, aspirin) for 24 hours\n• Mild swelling/bruising is normal for 3-7 days" },
  { label: "Microneedling", text: "• Keep skin clean and avoid makeup for 24 hours\n• Use SPF 30+ daily for 2 weeks\n• No active skincare (retinol, AHA/BHA) for 5 days\n• Mild redness is normal for 1-3 days\n• Drink lots of water" },
  { label: "PRP/PRF", text: "• Avoid washing the area for 6 hours\n• No exercise or sweating for 24 hours\n• Avoid sun, sauna, hot baths for 48 hours\n• Mild swelling/redness is normal for 2-3 days\n• Full results visible in 4-6 weeks" },
];

export default function TreatmentRecord({ appointment, client, onClose, onSaved }) {
  const [tab, setTab] = useState("chart");
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  // ★ Default starting amount is 1 (per owner). Was 20 — that made sense
  //   only for Botox; for fillers (now in ml), lasers (passes), wellness
  //   (ml), starting at 20 felt random. 1 is the right neutral baseline.
  const [pendingAmount, setPendingAmount] = useState(1);

  const [markers, setMarkers] = useState([]);
  const [view, setView] = useState("front");
  const [editingMarker, setEditingMarker] = useState(null);

  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoType, setPhotoType] = useState("before");
  const [photoView, setPhotoView] = useState("front");

  const [providerName, setProviderName] = useState("");
  const [clinicalNotes, setClinicalNotes] = useState("");
  const [postCareNotes, setPostCareNotes] = useState("");

  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");

  // ════════════════════════════════════════════════════════════════════
  // ★★★ BULLETPROOF CHARTING — owner asked for zero-loss guarantee. ★★★
  //
  // 6 layers of safety, in order of how they fire:
  //   (1) localStorage autosave — every keystroke / marker change goes
  //       to chart_draft_<appt_id> within ~600ms (debounced).
  //   (2) Draft recovery — on open, if localStorage has a draft AND the
  //       DB row is empty (or older), we restore the draft and show a
  //       banner so the nurse knows.
  //   (3) Unsaved-changes indicator — a red "● UNSAVED" pulses in the
  //       header. Goes green "✓ Saved" after a successful write.
  //   (4) beforeunload guard — if the nurse closes the tab with unsaved
  //       changes, the browser shows "Are you sure?".
  //   (5) Audit log — every save attempt (success OR failure) writes a
  //       row to treatment_chart_history. Recovery from DB-level loss is
  //       possible by replaying the latest success row.
  //   (6) Post-save verification — re-read the row, compare each field,
  //       throw if anything didn't persist. Already in save() below.
  // ════════════════════════════════════════════════════════════════════
  const DRAFT_KEY = `chart_draft_${appointment?.id || "unknown"}`;
  const [draftLastSaved, setDraftLastSaved] = useState(null); // localStorage time
  const [dbLastSaved, setDbLastSaved] = useState(null);       // DB time
  const [recoveredFromDraft, setRecoveredFromDraft] = useState(false);
  const [dirty, setDirty] = useState(false); // unsaved changes flag
  const skipNextDraftWriteRef = useRef(false);

  // (1) Autosave to localStorage — debounced 600ms. Watches the full set
  //     of editable state. Skips writes during the initial load.
  useEffect(() => {
    if (skipNextDraftWriteRef.current) {
      skipNextDraftWriteRef.current = false;
      return;
    }
    if (!appointment?.id) return;
    const t = setTimeout(() => {
      try {
        const payload = {
          appointment_id: appointment.id,
          client_id: client?.id || appointment.cid || null,
          provider_name: providerName,
          clinical_notes: clinicalNotes,
          post_care_notes: postCareNotes,
          markers,
          saved_at: new Date().toISOString(),
        };
        localStorage.setItem(DRAFT_KEY, JSON.stringify(payload));
        setDraftLastSaved(payload.saved_at);
        // Anything in state that's not yet in the last-known DB state is "dirty"
        setDirty(true);
      } catch (e) {
        // localStorage can be full or disabled (private mode). Best effort.
        console.warn("[chart] draft autosave failed:", e);
      }
    }, 600);
    return () => clearTimeout(t);
  }, [providerName, clinicalNotes, postCareNotes, markers, appointment?.id, DRAFT_KEY, client?.id]);

  // ★ Draft garbage collection — drafts older than 30 days are stale
  //   (the appointment is long past); prune them so localStorage doesn't
  //   accumulate forever. Covers both chart and consent draft keys.
  useEffect(() => {
    try {
      const cutoff = Date.now() - 30 * 86400000;
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (!k || (!k.startsWith("chart_draft_") && !k.startsWith("consent_draft_"))) continue;
        try {
          const d = JSON.parse(localStorage.getItem(k));
          const t = d?.saved_at ? new Date(d.saved_at).getTime() : (d?.ts || 0);
          if (!t || t < cutoff) localStorage.removeItem(k);
        } catch (_) { localStorage.removeItem(k); } // corrupt → remove
      }
    } catch (_) { /* private mode etc. */ }
  }, []);

  // (4) beforeunload guard — browser native warning on tab close with
  //     unsaved changes. Doesn't fire on react-router navigation, only
  //     on real tab close / refresh.
  useEffect(() => {
    const handler = (e) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = "You have unsaved chart changes. Leave anyway?";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // ── Load initial data ─────────────────────────────
  useEffect(() => {
    (async () => {
      // ★ Pull the chart picker list from the SERVICES table — that way
      //   any new service the owner adds in Services Manager shows up here
      //   automatically. We skip Facials, Retail, and Memberships per the
      //   owner's spec (those don't get charted). Each service is converted
      //   into a marker-compatible product row using category-based defaults
      //   for color and unit_label, so the existing chart engine keeps
      //   working unchanged.
      const SKIP_CATEGORIES = new Set(["Facials", "Memberships"]);
      const CATEGORY_DEFAULTS = {
        "Injectables":       { color: "#b48c3c", unit_label: "units",   display_order: 1, group: "Injectables" },
        "Laser Treatments":  { color: "#2563eb", unit_label: "passes",  display_order: 2, group: "Laser Treatments" },
        "Skin Treatments":   { color: "#7c3aed", unit_label: "sessions",display_order: 3, group: "Skin Treatments" },
        "Treatment Add-ons": { color: "#d9b878", unit_label: "applied", display_order: 4, group: "Treatment Add-ons" },
        "Health + Wellness": { color: "#2e8b57", unit_label: "ml",      display_order: 5, group: "Health + Wellness" },
        "Weightloss":        { color: "#0d9488", unit_label: "mg",      display_order: 6, group: "Weightloss" },
        "Hair Restoration":  { color: "#ec4899", unit_label: "ml",      display_order: 7, group: "Hair Restoration" },
      };
      // ★ Per-service overrides — fillers get "ml" per owner's 2026-06-10
      //   update. Tolerant of singular/plural: "Filler" and "Fillers" both
      //   match. Sculptra/Radiesse in "vials". PRP in "tubes".
      //   Earlier the trailing \b kept "Dermal Fillers" from matching
      //   because the boundary fell after the 's'.
      const NAME_UNIT_OVERRIDES = [
        { match: /(dermal\s*fillers?|lip\s*fillers?|cheek\s*fillers?|jawline\s*fillers?|tear\s*trough|under\s*eye|\bfillers?\b)/i, unit: "ml" },
        { match: /\b(sculptra|radiesse)s?\b/i, unit: "vials" },
        { match: /\b(prp|prf|prfm)\b/i, unit: "tubes" },
      ];
      const { data: svcs } = await supabase.from("services")
        .select("id,name,category,duration_minutes,price")
        .eq("active", true).order("category").order("name");
      const chartable = (svcs || [])
        .filter(s => !SKIP_CATEGORIES.has(s.category || "") && !/^Retail/i.test(s.category || ""))
        .map(s => {
          const cat = CATEGORY_DEFAULTS[s.category] || { color: "#b48c3c", unit_label: "units", display_order: 99, group: s.category || "Other" };
          // Per-name override beats per-category default (filler ≠ units).
          const override = NAME_UNIT_OVERRIDES.find(o => o.match.test(s.name || ""));
          const unit_label = override?.unit || cat.unit_label;
          return {
            id: s.id,
            name: s.name,
            color: cat.color,
            unit_label,
            display_order: cat.display_order,
            category: cat.group,        // chart UI uses category for the section header
            default_price: s.price || 0,
            is_active: true,
            duration_minutes: s.duration_minutes,
          };
        })
        .sort((a, b) =>
          (a.display_order - b.display_order) || a.name.localeCompare(b.name)
        );

      // ★ Always-available injectables — Daxxify and Xeomin are alternative
      //   neurotoxins (like Botox) that staff need to chart even before
      //   anyone adds them to the services catalog. Inject them as fallbacks
      //   if the services table doesn't already include them.
      const ALWAYS_INJECTABLES = [
        { name: "Daxxify",  color: "#b48c3c", unit_label: "units", display_order: 1, category: "Injectables", default_price: 0, is_active: true, duration_minutes: 30 },
        { name: "Xeomin",   color: "#b48c3c", unit_label: "units", display_order: 1, category: "Injectables", default_price: 0, is_active: true, duration_minutes: 30 },
      ];
      const existingLower = new Set(chartable.map(p => (p.name || "").toLowerCase()));
      ALWAYS_INJECTABLES.forEach(extra => {
        if (!existingLower.has(extra.name.toLowerCase())) {
          chartable.push({ ...extra, id: "fallback:" + extra.name.toLowerCase() });
        }
      });
      // Re-sort with the fallbacks merged in
      chartable.sort((a, b) =>
        (a.display_order - b.display_order) || (a.name || "").localeCompare(b.name || "")
      );

      setProducts(chartable);
      if (chartable.length) {
        const botox = chartable.find(p => p.name === "Botox");
        setSelectedProduct(botox || chartable[0]);
      }

      // Load existing record (if appointment was already recorded, allow editing)
      // ────────────────────────────────────────────────────────────────
      // We seed initial state from the DB row, then check localStorage for
      // a draft. If the draft is newer than the DB row OR the DB row is
      // empty, we restore the draft and surface a banner so the nurse
      // knows where the data came from. skipNextDraftWriteRef makes sure
      // the autosave effect doesn't re-write what we just loaded.
      // ────────────────────────────────────────────────────────────────
      const dbProviderName = appointment.provider_name || appointment.prov || "";
      const dbClinicalNotes = appointment.clinical_notes || "";
      const dbPostCareNotes = appointment.post_care_notes || "";
      const dbMarkers = appointment.chart_markers || [];
      const dbSavedAt = appointment.treatment_recorded_at || null;

      // (2) Draft recovery — only restore if the draft is newer or DB is empty
      let usedDraft = false;
      try {
        const raw = localStorage.getItem(DRAFT_KEY);
        if (raw) {
          const draft = JSON.parse(raw);
          const draftTime = draft.saved_at ? new Date(draft.saved_at).getTime() : 0;
          const dbTime = dbSavedAt ? new Date(dbSavedAt).getTime() : 0;
          const draftHasContent =
            (draft.clinical_notes || "").length > 0 ||
            (draft.post_care_notes || "").length > 0 ||
            (Array.isArray(draft.markers) && draft.markers.length > 0);
          if (draftHasContent && draftTime > dbTime) {
            skipNextDraftWriteRef.current = true;
            setProviderName(draft.provider_name || dbProviderName);
            setClinicalNotes(draft.clinical_notes || dbClinicalNotes);
            setPostCareNotes(draft.post_care_notes || dbPostCareNotes);
            setMarkers(Array.isArray(draft.markers) ? draft.markers : dbMarkers);
            setRecoveredFromDraft(true);
            setDraftLastSaved(draft.saved_at);
            setDirty(true);
            usedDraft = true;
          }
        }
      } catch (e) { console.warn("[chart] draft recovery failed:", e); }

      if (!usedDraft) {
        skipNextDraftWriteRef.current = true;
        if (dbMarkers.length) setMarkers(dbMarkers);
        if (dbProviderName) setProviderName(dbProviderName);
        if (dbClinicalNotes) setClinicalNotes(dbClinicalNotes);
        if (dbPostCareNotes) setPostCareNotes(dbPostCareNotes);
        setDbLastSaved(dbSavedAt);
        setDirty(false);
      }

      // Load photos for this appointment
      const { data: pics } = await supabase.from("treatment_photos")
        .select("*").eq("appointment_id", appointment.id).order("display_order");
      setPhotos(pics || []);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointment.id]);

  // ── Marker actions ─────────────────────────────
  const addMarkerAt = (x, y, vw) => {
    if (!selectedProduct) {
      alert("Pick a product first");
      return;
    }
    const newMarker = {
      id: crypto.randomUUID(),
      x: +x.toFixed(2), y: +y.toFixed(2), view: vw,
      product: selectedProduct.name,
      product_id: selectedProduct.id,
      amount: +pendingAmount,
      unit: selectedProduct.unit_label,
      area: "",
      color: selectedProduct.color,
      lot_number: "",
      notes: "",
      price: selectedProduct.default_price ? +(selectedProduct.default_price * +pendingAmount).toFixed(2) : 0,
    };
    setMarkers([...markers, newMarker]);
  };

  const updateMarker = (id, updates) => {
    setMarkers(markers.map(m => m.id === id ? { ...m, ...updates } : m));
  };

  const deleteMarker = (id) => {
    setMarkers(markers.filter(m => m.id !== id));
    if (editingMarker?.id === id) setEditingMarker(null);
  };

  // ── Photo actions ─────────────────────────────
  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const ext = file.name.split(".").pop().toLowerCase();
      const filename = `${crypto.randomUUID()}.${ext}`;
      const path = `${client.id}/${appointment.id}/${filename}`;

      const { error: upErr } = await supabase.storage
        .from("treatment-photos")
        .upload(path, file, { contentType: file.type, upsert: false });
      if (upErr) throw upErr;

      const { data: urlData } = supabase.storage
        .from("treatment-photos")
        .getPublicUrl(path);

      const { data: row, error: rowErr } = await supabase.from("treatment_photos").insert({
        appointment_id: appointment.id,
        client_id: client.id,
        url: urlData.publicUrl,
        storage_path: path,
        type: photoType,
        view: photoView,
        visible_to_client: true,
      }).select().single();
      if (rowErr) throw rowErr;

      setPhotos([...photos, row]);
    } catch (e) {
      alert("Upload failed: " + (e.message || e));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const updatePhoto = async (id, updates) => {
    // ★ Check the Supabase error — previously this swallowed failures
    //   silently and the UI flipped the toggle as if it had saved.
    const { error } = await supabase.from("treatment_photos").update(updates).eq("id", id);
    if (error) {
      alert("Failed to save photo update: " + error.message + "\n\nThe UI hasn't been changed. Try again.");
      return;
    }
    setPhotos(photos.map(p => p.id === id ? { ...p, ...updates } : p));
  };

  const deletePhoto = async (photo) => {
    if (!window.confirm("Delete this photo? This cannot be undone.")) return;
    const { error } = await supabase.from("treatment_photos").delete().eq("id", photo.id);
    if (error) {
      alert("Failed to delete photo: " + error.message);
      return;
    }
    if (photo.storage_path) {
      await supabase.storage.from("treatment-photos").remove([photo.storage_path]).catch(()=>{});
    }
    setPhotos(photos.filter(p => p.id !== photo.id));
  };

  // ── Save everything ─────────────────────────────
  const save = async () => {
    setSaving(true);
    const startedAt = new Date().toISOString();
    let savedByName = null;
    let savedByUserId = null;

    // Capture who's saving — used for both the appointments row and the
    // audit log row. Doing this once up-front so an auth glitch doesn't
    // partially-stamp the records.
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.id) {
        savedByUserId = user.id;
        const { data: prof } = await supabase.from("profiles").select("full_name,email").eq("id", user.id).maybeSingle();
        savedByName = prof?.full_name || prof?.email || user.email || "Staff";
      }
    } catch (_) { /* no auth — proceed unstamped */ }

    const updatePayload = {
      provider_name: providerName || null,
      clinical_notes: clinicalNotes || null,
      post_care_notes: postCareNotes || null,
      chart_markers: markers,
      treatment_recorded: true,
      treatment_recorded_at: startedAt,
    };
    if (savedByUserId) updatePayload.treatment_recorded_by_user_id = savedByUserId;
    if (savedByName)   updatePayload.treatment_recorded_by_name = savedByName;

    // ── Audit row helper — fires regardless of outcome so we always have
    //    a permanent trail of what was attempted. Best-effort; we never
    //    block the user save if audit fails.
    const writeAudit = async (outcome, errorText = null) => {
      try {
        await supabase.from("treatment_chart_history").insert({
          appointment_id: appointment.id,
          client_id: client?.id || appointment.cid || null,
          saved_by_user_id: savedByUserId,
          saved_by_name: savedByName,
          outcome,
          error_text: errorText ? String(errorText).slice(0, 1000) : null,
          provider_name: providerName || null,
          clinical_notes: clinicalNotes || null,
          post_care_notes: postCareNotes || null,
          chart_markers: markers,
          source: recoveredFromDraft ? "autosave_recovery" : "manual",
        });
      } catch (e) {
        console.warn("[chart] audit insert failed:", e);
      }
    };

    try {
      // ★ PRE-FLIGHT audit row (audit fix 2026-06-10) — written BEFORE the
      //   network call. If the iPad's WiFi drops mid-UPDATE, this row +
      //   the localStorage draft are the forensic record. Outcome
      //   'attempted' was added to the table's CHECK constraint today.
      await writeAudit("attempted");

      // ★ CONCURRENT-EDIT GUARD — if another staff member saved this chart
      //   AFTER we loaded it, don't silently clobber their work.
      try {
        const { data: freshRow } = await supabase
          .from("appointments")
          .select("treatment_recorded_at, treatment_recorded_by_name")
          .eq("id", appointment.id)
          .maybeSingle();
        const loadedAt = appointment.treatment_recorded_at ? new Date(appointment.treatment_recorded_at).getTime() : 0;
        const freshAt = freshRow?.treatment_recorded_at ? new Date(freshRow.treatment_recorded_at).getTime() : 0;
        if (freshAt > loadedAt) {
          const who = freshRow?.treatment_recorded_by_name || "another staff member";
          if (!window.confirm(`⚠️ This chart was saved by ${who} AFTER you opened it.\n\nSaving now will OVERWRITE their version (your localStorage draft + the audit log keep both).\n\nContinue and overwrite?`)) {
            setSaving(false);
            return;
          }
        }
      } catch (_) { /* lock check is best-effort */ }

      const { data, error: updErr } = await supabase
        .from("appointments")
        .update(updatePayload)
        .eq("id", appointment.id)
        .select("id, chart_markers, clinical_notes, post_care_notes, treatment_recorded, treatment_recorded_at, treatment_recorded_by_name");

      if (updErr) throw new Error(updErr.message || "DB rejected the update");
      if (!data || data.length === 0) throw new Error("No row was updated — appointment may have been deleted or you don't have permission");

      const written = data[0];
      // ─── (6) Post-save verification — re-read fields and compare.
      const issues = [];
      if (Array.isArray(markers) && markers.length > 0 && (!written.chart_markers || written.chart_markers.length !== markers.length)) {
        issues.push("chart_markers");
      }
      if (clinicalNotes && (written.clinical_notes || "") !== clinicalNotes) {
        issues.push("clinical_notes");
      }
      if (postCareNotes && (written.post_care_notes || "") !== postCareNotes) {
        issues.push("post_care_notes");
      }
      if (!written.treatment_recorded) {
        issues.push("treatment_recorded flag");
      }
      if (issues.length > 0) {
        // Audit a failure so we can replay if needed
        await writeAudit("failed", "Saved but these fields didn't persist: " + issues.join(", "));
        throw new Error("Saved but these fields didn't persist: " + issues.join(", ") + ". Take a screenshot and tell ops.");
      }

      // Persist audit success row + clear the draft + reset dirty flag
      await writeAudit("success");
      try { localStorage.removeItem(DRAFT_KEY); } catch (_) {}
      setDirty(false);
      setRecoveredFromDraft(false);
      setDraftLastSaved(null);
      setDbLastSaved(written.treatment_recorded_at);

      setSavedMsg("✓ Saved!");
      setTimeout(() => setSavedMsg(""), 2000);
      onSaved?.();
    } catch (e) {
      console.error("[TreatmentRecord] save error:", e);
      await writeAudit("failed", (e && e.message) || String(e));
      alert("Save failed: " + (e.message || e) + "\n\nDon't worry — your work is auto-saved in the browser. Reload and try again, or call the developer.");
    } finally {
      setSaving(false);
    }
  };

  const totalCost = markers.reduce((s, m) => s + (m.price || 0), 0);
  const clientName = client.name || `${client.first_name || ""} ${client.last_name || ""}`.trim();

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:2500,display:"flex",alignItems:"stretch",justifyContent:"center",backdropFilter:"blur(4px)"}}>
      <div onClick={e=>e.stopPropagation()} style={{background:C.bg,width:"100%",maxWidth:1180,height:"100vh",overflow:"auto",display:"flex",flexDirection:"column"}}>

        {/* Header */}
        <div style={{padding:"14px 22px",borderBottom:"1px solid "+C.border,background:"linear-gradient(135deg,"+C.goldBg+",rgba(180,140,60,0.04))",position:"sticky",top:0,zIndex:30,boxShadow:"0 1px 4px rgba(0,0,0,0.04)"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:14,flexWrap:"wrap"}}>
            <div style={{flex:1,minWidth:200}}>
              <div style={{fontSize:11,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:3}}>📋 Treatment Record</div>
              <div style={{fontSize:20,fontWeight:800,color:C.text,fontFamily:S}}>{clientName}</div>
              {/* ★ Client phone + email — visible to the provider during treatment without having to flip back to the client page */}
              {(client?.phone || client?.email) && (
                <div style={{fontSize:11,color:C.muted,marginTop:2,display:"flex",gap:12,flexWrap:"wrap"}}>
                  {client.phone && <a href={`tel:${String(client.phone).replace(/[^\d+]/g,"")}`} style={{color:C.gold,fontWeight:600,textDecoration:"none"}}>📞 {client.phone}</a>}
                  {client.email && <span>📧 {client.email}</span>}
                </div>
              )}
              <div style={{fontSize:11,color:C.muted,marginTop:2}}>
                {appointment.treat || "Visit"} · {fmtAppointmentDate(appointment.date,{weekday:"short",month:"short",day:"numeric",year:"numeric"})}
                {appointment.time && <> · {appointment.time}</>}
              </div>
            </div>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              {/* (3) Visual save indicator — pulsing red if dirty, green if saved */}
              {dirty ? (
                <span title="You have unsaved changes — autosaved locally in your browser" style={{
                  fontSize:10,fontWeight:800,color:"#fff",padding:"4px 10px",
                  background:"#dc2626",borderRadius:50,letterSpacing:"0.08em",
                  display:"flex",alignItems:"center",gap:5,
                  animation:"chart-pulse 1.4s ease-in-out infinite",
                }}>
                  <span style={{width:6,height:6,borderRadius:"50%",background:"#fff"}}/>
                  UNSAVED
                </span>
              ) : (dbLastSaved && (
                <span title={`Saved to database at ${new Date(dbLastSaved).toLocaleTimeString()}`} style={{
                  fontSize:10,fontWeight:800,color:C.green,padding:"4px 10px",
                  background:C.greenL,borderRadius:50,letterSpacing:"0.08em",
                  border:"1px solid "+C.green+"40",
                }}>
                  ✓ SAVED
                </span>
              ))}
              <style>{`@keyframes chart-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.55; } }`}</style>
              {savedMsg && <span style={{fontSize:11,color:C.green,fontWeight:700,padding:"5px 10px",background:C.greenL,borderRadius:6}}>{savedMsg}</span>}
              <button onClick={save} disabled={saving} style={{padding:"10px 22px",background:saving?C.light:"linear-gradient(135deg,"+C.green+",#1f6b40)",color:saving?C.muted:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:saving?"not-allowed":"pointer",fontFamily:F,boxShadow:"0 2px 8px rgba(46,139,87,0.25)"}}>
                {saving ? "Saving..." : "💾 Save Treatment"}
              </button>
              <button onClick={() => {
                if (dirty && !window.confirm("You have unsaved changes. Close anyway?\n\nYour work is autosaved in this browser and will be recoverable when you reopen.")) return;
                onClose && onClose();
              }} style={{padding:"10px 14px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:8,fontSize:13,cursor:"pointer",fontFamily:F}}>Close</button>
            </div>
          </div>

          {/* (2) Recovery banner — shows when we restored from localStorage */}
          {recoveredFromDraft && (
            <div style={{
              marginTop:10,padding:"10px 14px",background:"rgba(180,140,60,0.10)",
              border:"1px solid "+C.gold+"60",borderRadius:8,
              display:"flex",alignItems:"center",gap:10,fontSize:12,color:C.text,
            }}>
              <span style={{fontSize:16}}>♻️</span>
              <div style={{flex:1}}>
                <strong style={{color:C.gold}}>Recovered unsaved chart</strong>
                {draftLastSaved && <> — autosaved at <strong>{new Date(draftLastSaved).toLocaleString()}</strong></>}
                <div style={{fontSize:10.5,color:C.muted,marginTop:2}}>
                  Your last edits weren't fully saved to the database, but this browser kept a copy. Review and click "Save Treatment" to commit.
                </div>
              </div>
              <button onClick={() => {
                if (window.confirm("Discard the recovered draft and load the original database version?\n\nThis cannot be undone.")) {
                  try { localStorage.removeItem(DRAFT_KEY); } catch(_){}
                  // Reset to DB state — reload appointment data effectively
                  skipNextDraftWriteRef.current = true;
                  setMarkers(appointment.chart_markers || []);
                  setProviderName(appointment.provider_name || appointment.prov || "");
                  setClinicalNotes(appointment.clinical_notes || "");
                  setPostCareNotes(appointment.post_care_notes || "");
                  setRecoveredFromDraft(false);
                  setDirty(false);
                }
              }} style={{padding:"4px 10px",background:"transparent",color:C.muted,border:"1px solid "+C.border,borderRadius:6,fontSize:10,fontWeight:600,cursor:"pointer",fontFamily:F}}>Discard</button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div style={{padding:"10px 22px",background:C.surface,borderBottom:"1px solid "+C.border,position:"sticky",top:78,zIndex:25}}>
          <div style={{display:"flex",gap:6}}>
            {[
              ["chart", `🎨 Chart (${markers.length})`],
              ["photos", `📸 Photos (${photos.length})`],
              ["notes", "📝 Notes"],
            ].map(([id, label]) => (
              <button key={id} onClick={()=>setTab(id)} style={{
                padding:"9px 18px",
                background: tab === id ? "linear-gradient(135deg,"+C.gold+","+C.goldD+")" : "transparent",
                color: tab === id ? "#fff" : C.muted,
                border:"none", borderRadius:8,
                fontSize:12, fontWeight:tab===id?700:600,
                cursor:"pointer", fontFamily:F, transition:"all 0.15s",
              }}>{label}</button>
            ))}
            <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:8}}>
              {totalCost > 0 && (
                <div style={{fontSize:11,color:C.gold,fontWeight:700,padding:"5px 12px",background:C.goldBg,borderRadius:6,border:"1px solid "+C.borderGold}}>
                  Products total: ${totalCost.toFixed(2)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{flex:1,padding:"18px 22px",display:"flex",gap:18,flexWrap:"wrap"}}>
          {tab === "chart" && (
            <ChartTab
              products={products}
              selectedProduct={selectedProduct}
              setSelectedProduct={setSelectedProduct}
              pendingAmount={pendingAmount}
              setPendingAmount={setPendingAmount}
              markers={markers}
              view={view}
              setView={setView}
              addMarkerAt={addMarkerAt}
              editingMarker={editingMarker}
              setEditingMarker={setEditingMarker}
              updateMarker={updateMarker}
              deleteMarker={deleteMarker}
            />
          )}
          {tab === "photos" && (
            <PhotosTab
              photos={photos}
              uploading={uploadingPhoto}
              photoType={photoType}
              setPhotoType={setPhotoType}
              photoView={photoView}
              setPhotoView={setPhotoView}
              onUpload={handleFileUpload}
              onUpdate={updatePhoto}
              onDelete={deletePhoto}
            />
          )}
          {tab === "notes" && (
            <NotesTab
              providerName={providerName}
              setProviderName={setProviderName}
              clinicalNotes={clinicalNotes}
              setClinicalNotes={setClinicalNotes}
              postCareNotes={postCareNotes}
              setPostCareNotes={setPostCareNotes}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// CHART TAB
// ═══════════════════════════════════════════════════════════
function ChartTab({ products, selectedProduct, setSelectedProduct, pendingAmount, setPendingAmount, markers, view, setView, addMarkerAt, editingMarker, setEditingMarker, updateMarker, deleteMarker }) {
  // Group products by category for the picker
  const byCategory = {};
  products.forEach(p => {
    const cat = p.category || "other";
    if (!byCategory[cat]) byCategory[cat] = [];
    byCategory[cat].push(p);
  });
  // ★ Category labels for the chart product picker. Now pulled from
  //   services-table category names, with emoji prefixes for scanability.
  const categoryLabels = {
    "Injectables":       "💉 Injectables",
    "Laser Treatments":  "🔬 Laser Treatments",
    "Skin Treatments":   "✨ Skin Treatments",
    "Treatment Add-ons": "➕ Add-ons",
    "Health + Wellness": "💧 Health & Wellness",
    "Weightloss":        "💊 Weight Loss",
    "Hair Restoration":  "💇 Hair Restoration",
    // legacy (treatment_products) fallbacks — kept so old rows still render
    neurotoxin: "💉 Neurotoxins", filler: "✨ Fillers",
    biostimulator: "🧬 Biostimulators", other: "🔬 Other",
  };

  return (
    <>
      {/* LEFT: face chart */}
      <div style={{flex:"1 1 360px",minWidth:300}}>
        <FaceChart
          markers={markers}
          view={view}
          onViewChange={setView}
          onCanvasClick={(x,y,vw)=>addMarkerAt(x,y,vw)}
          onMarkerClick={(m)=>setEditingMarker(m)}
          size="lg"
        />
        {selectedProduct && (
          <div style={{marginTop:14,padding:"12px 14px",background:C.surface,border:"2px solid "+selectedProduct.color,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <div style={{width:14,height:14,borderRadius:"50%",background:selectedProduct.color,boxShadow:"0 0 0 3px "+selectedProduct.color+"30"}}/>
              <div>
                <div style={{fontSize:11,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em"}}>Currently dropping</div>
                <div style={{fontSize:14,fontWeight:700,color:C.text}}>{selectedProduct.name} @ {pendingAmount} {selectedProduct.unit_label}</div>
              </div>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              {/* Step matches the unit: ml=0.5, units=1, everything else=1 */}
              {(() => {
                const step = selectedProduct.unit_label === "ml" ? 0.5 : 1;
                return <>
                  <button onClick={()=>setPendingAmount(Math.max(0, +(pendingAmount - step).toFixed(2)))} style={smallBtn}>−</button>
                  <input type="number" step={step} value={pendingAmount} onChange={e=>setPendingAmount(+e.target.value)} style={{...inp,width:70,textAlign:"center",fontWeight:700}}/>
                  <button onClick={()=>setPendingAmount(+(pendingAmount + step).toFixed(2))} style={smallBtn}>+</button>
                </>;
              })()}
            </div>
          </div>
        )}
      </div>

      {/* RIGHT: products + markers */}
      <div style={{flex:"1 1 340px",minWidth:280,display:"flex",flexDirection:"column",gap:14}}>
        {/* Product picker */}
        <Section title="Pick product, then tap face">
          {Object.entries(byCategory).map(([cat, list]) => (
            <div key={cat} style={{marginBottom:10}}>
              <div style={{fontSize:10,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:5}}>{categoryLabels[cat] || cat}</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                {list.map(p => {
                  const isSel = selectedProduct?.id === p.id;
                  return (
                    <button key={p.id} onClick={()=>{ setSelectedProduct(p); setPendingAmount(1); }} style={{
                      padding:"7px 12px",
                      background: isSel ? p.color : "#fff",
                      color: isSel ? "#fff" : C.text,
                      border: "1.5px solid " + p.color,
                      borderRadius: 50,
                      fontSize: 11, fontWeight: isSel ? 700 : 600,
                      cursor: "pointer", fontFamily: F,
                      display: "flex", alignItems: "center", gap: 6,
                      boxShadow: isSel ? "0 2px 8px " + p.color + "60" : "none",
                      transition: "all 0.15s",
                    }}>
                      <span style={{width:8,height:8,borderRadius:"50%",background: isSel ? "#fff" : p.color}}/>
                      {p.name}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </Section>

        {/* Marker list */}
        <Section title={`Markers (${markers.length})`}>
          {markers.length === 0 ? (
            <div style={{padding:18,textAlign:"center",background:C.light,borderRadius:8,fontSize:11,color:C.muted,border:"1px dashed "+C.border}}>
              No markers yet. Pick a product above and tap on the face.
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:380,overflow:"auto"}}>
              {markers.map((m, i) => (
                <MarkerListItem
                  key={m.id}
                  marker={m}
                  index={i + 1}
                  isEditing={editingMarker?.id === m.id}
                  onClick={()=>setEditingMarker(editingMarker?.id === m.id ? null : m)}
                  onUpdate={(updates)=>updateMarker(m.id, updates)}
                  onDelete={()=>deleteMarker(m.id)}
                />
              ))}
            </div>
          )}
        </Section>
      </div>
    </>
  );
}

const smallBtn = {padding:"6px 10px",background:C.light,color:C.text,border:"1px solid "+C.border,borderRadius:6,fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:F,minWidth:32};

function MarkerListItem({ marker, index, isEditing, onClick, onUpdate, onDelete }) {
  return (
    <div style={{
      padding: isEditing ? "12px" : "9px 12px",
      background: isEditing ? "#fff" : C.light,
      border: "1px solid " + (isEditing ? marker.color : C.border),
      borderLeft: "3px solid " + marker.color,
      borderRadius: 8,
      transition: "all 0.15s",
    }}>
      <div onClick={onClick} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
        <div style={{width:24,height:24,borderRadius:"50%",background:marker.color,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,flexShrink:0}}>{index}</div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:12,fontWeight:700,color:C.text}}>{marker.product} <span style={{fontSize:11,color:C.muted}}>· {marker.amount}{marker.unit === "ml" ? "ml" : "u"}</span></div>
          <div style={{fontSize:10,color:C.muted}}>{marker.area || "(no area)"} · {marker.view}{marker.price ? " · $" + marker.price.toFixed(2) : ""}</div>
        </div>
        <button onClick={(e)=>{e.stopPropagation(); onDelete();}} style={{padding:"4px 8px",background:"transparent",color:C.red,border:"none",borderRadius:5,fontSize:14,cursor:"pointer"}}>🗑</button>
      </div>

      {isEditing && (
        <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid "+C.border,display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          <Field label="Amount">
            <input type="number" step="0.5" value={marker.amount || 0}
              onChange={e=>{
                const amt = +e.target.value;
                onUpdate({ amount: amt, price: marker.price ? +(marker.price / Math.max(marker.amount, 0.01) * amt).toFixed(2) : 0 });
              }} style={inp}/>
          </Field>
          <Field label="Area">
            <input value={marker.area || ""} onChange={e=>onUpdate({ area: e.target.value })} placeholder="e.g. Glabella" style={inp}/>
          </Field>
          <Field label="Lot Number">
            <input value={marker.lot_number || ""} onChange={e=>onUpdate({ lot_number: e.target.value })} placeholder="e.g. B12345" style={{...inp,fontFamily:"monospace",fontSize:12}}/>
          </Field>
          <Field label="Price">
            <input type="number" step="0.01" value={marker.price || 0} onChange={e=>onUpdate({ price: +e.target.value })} style={inp}/>
          </Field>
          <div style={{gridColumn:"1 / -1"}}>
            <Field label="Notes">
              <input value={marker.notes || ""} onChange={e=>onUpdate({ notes: e.target.value })} placeholder="Any specific notes for this site..." style={inp}/>
            </Field>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// PHOTOS TAB
// ═══════════════════════════════════════════════════════════
function PhotosTab({ photos, uploading, photoType, setPhotoType, photoView, setPhotoView, onUpload, onUpdate, onDelete }) {
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith("image/"));
    files.forEach(f => onUpload(f));
  };

  const beforePics = photos.filter(p => p.type === "before");
  const afterPics = photos.filter(p => p.type === "after");
  const otherPics = photos.filter(p => !["before","after"].includes(p.type));

  return (
    <div style={{flex:1,minWidth:0}}>
      {/* Upload zone */}
      <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
        <div style={{flex:"0 0 auto",display:"flex",gap:6,background:C.surface,padding:5,borderRadius:8,border:"1px solid "+C.border}}>
          {[
            ["before", "📸 Before", "#dc2626"],
            ["after", "✨ After", "#059669"],
            ["reference", "📋 Reference", "#6b7280"],
            ["progress", "📈 Progress", "#7c3aed"],
          ].map(([t, label, col]) => (
            <button key={t} onClick={()=>setPhotoType(t)} style={{
              padding:"7px 12px", background: photoType === t ? col : "transparent",
              color: photoType === t ? "#fff" : C.muted, border:"none", borderRadius:6,
              fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:F,
            }}>{label}</button>
          ))}
        </div>
        <select value={photoView} onChange={e=>setPhotoView(e.target.value)} style={{...inp,width:"auto"}}>
          <option value="front">Front</option>
          <option value="left">Left</option>
          <option value="right">Right</option>
          <option value="3q_left">3/4 Left</option>
          <option value="3q_right">3/4 Right</option>
          <option value="other">Other</option>
        </select>
      </div>

      <div
        onDragOver={e=>{e.preventDefault(); setDragOver(true);}}
        onDragLeave={()=>setDragOver(false)}
        onDrop={handleDrop}
        onClick={()=>fileRef.current?.click()}
        style={{
          padding:"30px 22px", textAlign:"center",
          background: dragOver ? C.goldBg : C.surface,
          border:"2px dashed " + (dragOver ? C.gold : C.borderGold),
          borderRadius:12, cursor:"pointer", marginBottom:18,
          transition:"all 0.15s",
        }}
      >
        <div style={{fontSize:32,marginBottom:8}}>{uploading ? "⏳" : "📤"}</div>
        <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:4}}>
          {uploading ? "Uploading..." : "Drop photos here or click to upload"}
        </div>
        <div style={{fontSize:11,color:C.muted}}>JPG, PNG, WebP, HEIC · Max 10MB · Will save as <strong>{photoType}</strong> · <strong>{photoView}</strong></div>
        <input ref={fileRef} type="file" accept="image/*" multiple style={{display:"none"}}
          onChange={e=>{ Array.from(e.target.files).forEach(f => onUpload(f)); e.target.value = ""; }}/>
      </div>

      {/* Photo groups */}
      <PhotoGroup title="📸 Before Photos" photos={beforePics} onUpdate={onUpdate} onDelete={onDelete} color="#dc2626"/>
      <PhotoGroup title="✨ After Photos" photos={afterPics} onUpdate={onUpdate} onDelete={onDelete} color="#059669"/>
      {otherPics.length > 0 && <PhotoGroup title="Other" photos={otherPics} onUpdate={onUpdate} onDelete={onDelete} color="#6b7280"/>}

      {photos.length === 0 && (
        <div style={{padding:30,textAlign:"center",color:C.muted,fontSize:12}}>No photos uploaded yet</div>
      )}
    </div>
  );
}

function PhotoGroup({ title, photos, onUpdate, onDelete, color }) {
  if (photos.length === 0) return null;
  return (
    <div style={{marginBottom:18}}>
      <div style={{fontSize:11,fontWeight:700,color,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>{title} ({photos.length})</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:10}}>
        {photos.map(p => (
          <div key={p.id} style={{background:C.surface,border:"1px solid "+C.border,borderRadius:10,overflow:"hidden",position:"relative"}}>
            <img src={p.url} alt={p.caption || p.type} style={{width:"100%",height:160,objectFit:"cover",display:"block",background:"#000"}}/>
            <div style={{padding:10}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                <span style={{fontSize:9,padding:"2px 8px",background:color+"20",color,borderRadius:50,fontWeight:700,textTransform:"uppercase"}}>{p.type} · {p.view || "—"}</span>
                <button onClick={()=>onDelete(p)} style={{padding:"2px 6px",background:"transparent",color:C.red,border:"none",fontSize:14,cursor:"pointer"}}>🗑</button>
              </div>
              <input value={p.caption || ""} onChange={e=>onUpdate(p.id, { caption: e.target.value })} placeholder="Add caption..."
                style={{width:"100%",padding:"5px 8px",background:C.light,border:"1px solid "+C.border,borderRadius:5,fontSize:10,fontFamily:F,outline:"none",boxSizing:"border-box",marginBottom:6}}/>
              <button onClick={()=>onUpdate(p.id, { visible_to_client: !p.visible_to_client })} style={{
                width:"100%",padding:"5px 8px",
                background: p.visible_to_client ? C.greenL : C.light,
                color: p.visible_to_client ? C.green : C.muted,
                border:"1px solid " + (p.visible_to_client ? C.green+"40" : C.border),
                borderRadius:5, fontSize:10, fontWeight:600, cursor:"pointer", fontFamily:F,
              }}>
                {p.visible_to_client ? "👁 Visible to client" : "🔒 Staff only"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// CLINICAL NOTES — multi-block with templates
// Provider can add as many note sections as they want. Each block has its
// own "+ Insert Template" button. All blocks serialize into one string
// separated by ── ── so the existing DB column (clinical_notes) stays
// compatible without a migration.
// ═══════════════════════════════════════════════════════════
const NOTE_BLOCK_SEPARATOR = "\n\n────────────\n\n";

function splitNoteBlocks(s) {
  if (!s) return [""];
  const parts = String(s).split(NOTE_BLOCK_SEPARATOR);
  return parts.length ? parts : [""];
}
function joinNoteBlocks(arr) {
  return arr.filter(b => b !== null && b !== undefined).join(NOTE_BLOCK_SEPARATOR);
}

function ClinicalNotesBlocks({ value, onChange }) {
  const [blocks, setBlocks] = useState(() => splitNoteBlocks(value));
  // Whenever the parent's `value` changes (e.g. loaded from DB after mount),
  // re-sync our internal block list.
  const lastSyncedRef = useRef(value);
  useEffect(() => {
    if (value !== lastSyncedRef.current) {
      setBlocks(splitNoteBlocks(value));
      lastSyncedRef.current = value;
    }
  }, [value]);
  const pushChange = (next) => {
    setBlocks(next);
    const joined = joinNoteBlocks(next);
    lastSyncedRef.current = joined;
    onChange(joined);
  };
  const updateAt = (i, v) => pushChange(blocks.map((b, j) => j === i ? v : b));
  const addBlock = () => pushChange([...blocks, ""]);
  const removeAt = (i) => pushChange(blocks.filter((_, j) => j !== i));

  return (
    <div style={{marginBottom:14}}>
      {blocks.map((b, i) => (
        <div key={i} style={{padding:18,background:C.surface,border:"1px solid "+C.border,borderRadius:10,marginBottom:10}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,flexWrap:"wrap"}}>
            <span style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S}}>📝 Clinical Notes{blocks.length > 1 ? ` · #${i + 1}` : ""}</span>
            <span style={{fontSize:9,padding:"2px 8px",background:C.redL,color:C.red,borderRadius:50,fontWeight:700,textTransform:"uppercase"}}>STAFF ONLY</span>
            <div style={{marginLeft:"auto",display:"flex",gap:6,alignItems:"center"}}>
              <NoteTemplatePicker
                onInsert={(body) => {
                  updateAt(i, b && b.trim() ? b.trimEnd() + "\n\n" + body : body);
                }}
              />
              {blocks.length > 1 && (
                <button type="button" onClick={() => {
                  if (!b || !b.trim() || window.confirm("Remove this note block?")) removeAt(i);
                }} title="Remove this note block"
                  style={{padding:"6px 10px",background:"#fff",color:C.red,border:"1px solid "+C.red+"40",borderRadius:6,fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:F}}>🗑</button>
              )}
            </div>
          </div>
          {i === 0 && <div style={{fontSize:11,color:C.muted,marginBottom:8}}>Internal notes - patient's reaction, complications, observations. NOT shown to client.</div>}
          <textarea value={b || ""} onChange={e => updateAt(i, e.target.value)} rows={10}
            placeholder={i === 0 ? "Patient tolerated procedure well. No complications. Slight bruising at left glabella site...&#10;&#10;💡 Use '+ Insert Template' above for ready-made notes (Botox, Lasers, Peels, etc.)" : "Add additional clinical observations or a second template…"}
            style={{...inp,resize:"vertical",fontFamily:F,lineHeight:1.5}}/>
        </div>
      ))}
      <button type="button" onClick={addBlock}
        style={{padding:"9px 16px",background:C.goldBg,color:C.goldD,border:`1px dashed ${C.borderGold}`,borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F,width:"100%"}}>
        + Add Another Note Block
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// NOTES TAB
// ═══════════════════════════════════════════════════════════
function NotesTab({ providerName, setProviderName, clinicalNotes, setClinicalNotes, postCareNotes, setPostCareNotes }) {
  return (
    <div style={{flex:1,minWidth:0,maxWidth:760,margin:"0 auto",width:"100%"}}>
      <div style={{padding:18,background:C.surface,border:"1px solid "+C.border,borderRadius:10,marginBottom:14}}>
        <Field label="👤 Treatment Provider">
          <input value={providerName} onChange={e=>setProviderName(e.target.value)} placeholder="Auto-filled from the appointment — edit only if a different provider performed it" style={inp}/>
          <div style={{fontSize:10,color:C.muted,marginTop:4}}>Auto-filled from this appointment. Override only if the actual provider differed from what was booked.</div>
        </Field>
      </div>

      <ClinicalNotesBlocks value={clinicalNotes} onChange={setClinicalNotes}/>

      <div style={{padding:18,background:C.surface,border:"1px solid "+C.border,borderRadius:10,marginBottom:14}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
          <span style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S}}>💖 Post-Care Instructions</span>
          <span style={{fontSize:9,padding:"2px 8px",background:C.greenL,color:C.green,borderRadius:50,fontWeight:700,textTransform:"uppercase"}}>VISIBLE TO CLIENT</span>
        </div>
        <div style={{fontSize:11,color:C.muted,marginBottom:8}}>Aftercare guidance the client will see in their portal.</div>

        <div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:8}}>
          {POST_CARE_TEMPLATES.map(t => (
            <button key={t.label} onClick={()=>setPostCareNotes(postCareNotes ? postCareNotes + "\n\n" + t.text : t.text)} style={{padding:"5px 11px",background:C.goldBg,color:C.gold,border:"1px solid "+C.borderGold,borderRadius:50,fontSize:10,fontWeight:600,cursor:"pointer",fontFamily:F}}>
              + {t.label}
            </button>
          ))}
        </div>

        <textarea value={postCareNotes} onChange={e=>setPostCareNotes(e.target.value)} rows={8} placeholder="Or type custom aftercare..." style={{...inp,resize:"vertical",fontFamily:F,lineHeight:1.5}}/>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════
function Section({ title, children }) {
  return (
    <div style={{padding:14,background:C.surface,border:"1px solid "+C.border,borderRadius:10}}>
      <div style={{fontSize:12,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10,fontFamily:S}}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{marginBottom:10}}>
      <label style={{display:"block",fontSize:10,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:4}}>{label}</label>
      {children}
    </div>
  );
}
